import type { IntlShape } from 'react-intl';
import type { FormFields } from '../../../models/newSupportCall';
import { getTasAdditionalRequiredFields, type RequiredFieldRule } from './tasAdditionalRequiredFields';

export type NewSupportCallFormErrors = Partial<Record<keyof FormFields, string>>;

type RequiredFieldMessageRule = {
    field: keyof FormFields;
    messageId: string;
};

const MIN_PHONE_DIGITS = 10;
const CHASSIS_LENGTH = 17;
const ADMINISTRATIVE_SUBJECTS = ['ADMINISTRATIVE', 'FINANCIAL', 'PORTAL', 'COMMERCIAL'];

const isBlank = (value: unknown) => {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    return false;
};

const addRequiredErrors = (
    errors: NewSupportCallFormErrors,
    formData: FormFields,
    rules: RequiredFieldRule[],
) => {
    rules.forEach(({ field, message }) => {
        if (isBlank(formData[field])) {
            errors[field] = message;
        }
    });
};

const validateChassis = (
    errors: NewSupportCallFormErrors,
    chassis: string | undefined,
    isRequired: boolean,
    intl: IntlShape,
) => {
    if (!chassis) {
        if (isRequired) {
            errors.chassis = intl.formatMessage({ id: 'intl-msg:support.validateNewSupportCallForm.chassisLength' });
        }
        return;
    }

    if (chassis.length !== CHASSIS_LENGTH) {
        errors.chassis = intl.formatMessage({ id: 'intl-msg:support.validateNewSupportCallForm.chassisLength' });
    }
};

const getPhoneDigitCount = (phone?: string) => (phone ?? '').replace(/\D/g, '').length;

const resolveRequiredFields = (intl: IntlShape, rules: RequiredFieldMessageRule[]): RequiredFieldRule[] =>
    rules.map(({ field, messageId }) => ({ field, message: intl.formatMessage({ id: messageId }) }));

const commonRequiredFields: RequiredFieldMessageRule[] = [
    { field: 'dn', messageId: 'intl-msg:support.validateNewSupportCallForm.dnRequired' },
    { field: 'regionalId', messageId: 'intl-msg:support.validateNewSupportCallForm.regionalRequired' },
];

const technicalRequiredFields: RequiredFieldMessageRule[] = [
    { field: 'dnResponsible', messageId: 'intl-msg:support.validateNewSupportCallForm.dnResponsibleRequired' },
    { field: 'licensePlate', messageId: 'intl-msg:support.validateNewSupportCallForm.licensePlateRequired' },
    { field: 'customer', messageId: 'intl-msg:support.validateNewSupportCallForm.customerRequired' },
    { field: 'hourMeter', messageId: 'intl-msg:support.validateNewSupportCallForm.hourMeterRequired' },
    { field: 'odometer', messageId: 'intl-msg:support.validateNewSupportCallForm.odometerRequired' },
    { field: 'entryDate', messageId: 'intl-msg:support.validateNewSupportCallForm.entryDateRequired' },
    { field: 'partName', messageId: 'intl-msg:support.validateNewSupportCallForm.partNameRequired' },
    { field: 'customerComment', messageId: 'intl-msg:support.validateNewSupportCallForm.customerCommentRequired' },
    { field: 'diagnose', messageId: 'intl-msg:support.validateNewSupportCallForm.diagnoseRequired' },
    { field: 'repair', messageId: 'intl-msg:support.validateNewSupportCallForm.repairRequired' },
    { field: 'analysisReason', messageId: 'intl-msg:support.validateNewSupportCallForm.analysisReasonRequired' },
];

const tasRequiredFields: RequiredFieldMessageRule[] = [
    { field: 'dnResponsible', messageId: 'intl-msg:support.validateNewSupportCallForm.dnResponsibleRequired' },
    { field: 'odometer', messageId: 'intl-msg:support.validateNewSupportCallForm.odometerRequired' },
];

const cancellationRequiredFields: RequiredFieldMessageRule[] = [
    { field: 'contractNumber', messageId: 'intl-msg:support.validateNewSupportCallForm.contractNumberRequired' },
    { field: 'vehicleQtd', messageId: 'intl-msg:support.validateNewSupportCallForm.vehicleQtdRequired' },
    { field: 'cancelReason', messageId: 'intl-msg:support.validateNewSupportCallForm.cancelReasonRequired' },
    { field: 'cancelComment', messageId: 'intl-msg:support.validateNewSupportCallForm.cancelCommentRequired' },
];

const administrativeRequiredFields: RequiredFieldMessageRule[] = [
    { field: 'customerComment', messageId: 'intl-msg:support.validateNewSupportCallForm.requestDetailRequired' },
];

export const validateNewSupportCallForm = (
    formData: FormFields,
    loggedUserName: string,
    intl: IntlShape,
): NewSupportCallFormErrors => {
    const errors: NewSupportCallFormErrors = {};
    const subject = formData.subject ?? '';
    const isTechnicalSubject = subject === 'TECHNICAL';
    const isTechnicalSupport = formData.supportType === 'TECHNICAL_SUPPORT';

    if (!loggedUserName && isBlank(formData.responsible)) {
        errors.responsible = intl.formatMessage({ id: 'intl-msg:support.validateNewSupportCallForm.responsibleRequired' });
    }

    addRequiredErrors(errors, formData, resolveRequiredFields(intl, commonRequiredFields));

    if (isBlank(formData.phone)) {
        errors.phone = intl.formatMessage({ id: 'intl-msg:support.validateNewSupportCallForm.phoneRequired' });
    } else if (getPhoneDigitCount(formData.phone) < MIN_PHONE_DIGITS) {
        errors.phone = intl.formatMessage({ id: 'intl-msg:support.validateNewSupportCallForm.phoneInvalid' });
    }

    validateChassis(errors, formData.chassis, isTechnicalSubject, intl);

    if (isTechnicalSupport) {
        addRequiredErrors(errors, formData, resolveRequiredFields(intl, tasRequiredFields));
    }

    if (isTechnicalSubject) {
        addRequiredErrors(errors, formData, resolveRequiredFields(intl, technicalRequiredFields));
        return errors;
    }

    if (ADMINISTRATIVE_SUBJECTS.includes(subject)) {
        addRequiredErrors(errors, formData, resolveRequiredFields(intl, administrativeRequiredFields));
        return errors;
    }

    if (subject === 'CONTRACT_CANCELLATION') {
        addRequiredErrors(errors, formData, resolveRequiredFields(intl, cancellationRequiredFields));
        return errors;
    }

    addRequiredErrors(errors, formData, getTasAdditionalRequiredFields(subject, intl));
    return errors;
};

export const getNewSupportCallValidationSummary = (errors: NewSupportCallFormErrors) =>
    Object.values(errors).filter(Boolean).join('; ');
